import { c as _c } from "react/compiler-runtime";
import { QueryKeys } from "@/constants/queryKeys";
import { keepPreviousData, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import useSession from "@/hooks/QueryHooks/useSession";
import { useInfinitePaginatedQuery } from "@/hooks/QueryHooks/useInfinitePaginatedQuery";
import { PERMISSION } from "@/generated/contracts";
import { getAdminListingsAction } from "../actions/listings.actions";
import type { AdminListingRow, AdminListingsQueryParams } from "../schemas";
export const ADMIN_LISTINGS_QUERY_KEYS = {
  all: [QueryKeys.ADMIN_LISTINGS],
  list: (params: AdminListingsQueryParams) => [...ADMIN_LISTINGS_QUERY_KEYS.all, "list", params],
  infinite: (params: Omit<AdminListingsQueryParams, "page">) => [...ADMIN_LISTINGS_QUERY_KEYS.all, "infinite", params]
};
export function getAdminListingsQueryOptions({
  enabled,
  ...params
}: AdminListingsQueryParams & {
  enabled: boolean;
}) {
  return queryOptions({
    queryKey: ADMIN_LISTINGS_QUERY_KEYS.list(params),
    queryFn: () => getAdminListingsAction({
      ...params,
      search: params.search || undefined
    }),
    enabled,
    placeholderData: keepPreviousData
  });
}
export function useAdminListings(params) {
  const $ = _c(5);
  const {
    data: session,
    isFetched
  } = useSession();
  let t0;
  let t1;
  if ($[0] !== isFetched || $[1] !== params || $[2] !== session?.permissions) {
    const isAdmin = (session?.permissions ?? []).includes(PERMISSION.BACKOFFICE);
    t0 = useQuery;
    t1 = getAdminListingsQueryOptions({
      enabled: isFetched && isAdmin,
      ...params
    });
    $[0] = isFetched;
    $[1] = params;
    $[2] = session?.permissions;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  return t0(t1);
}
export function useInfiniteAdminListings(params) {
  const $ = _c(12);
  const {
    data: session,
    isFetched
  } = useSession();
  let t0;
  if ($[0] !== session?.permissions) {
    t0 = session?.permissions ?? [];
    $[0] = session?.permissions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = t0.includes(PERMISSION.BACKOFFICE);
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isAdmin = t1;
  let t2;
  if ($[4] !== params) {
    t2 = ADMIN_LISTINGS_QUERY_KEYS.infinite(params);
    $[4] = params;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== params) {
    t3 = t4 => {
      const {
        page,
        limit
      } = t4;
      return getAdminListingsAction({
        ...params,
        page,
        limit,
        search: params.search || undefined
      });
    };
    $[6] = params;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isFetched && isAdmin;
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useInfinitePaginatedQuery(t5);
}
export function usePrefetchAdminListings() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = params => {
      const options = getAdminListingsQueryOptions({
        enabled: true,
        ...params
      });
      if (queryClient.getQueryData(options.queryKey)) {
        return;
      }
      queryClient.prefetchQuery(options);
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}